import type {
  Control,
  FieldArrayPath,
  FieldArrayPathValue,
  FieldValues,
  Path,
} from "react-hook-form";
import { Controller, useFieldArray } from "react-hook-form";

import { useCurrencies } from "@hooks/useCurrencies";
import { IAttribute, IAttributeFieldValue } from "@interfaces/attribute.interface";
import { ICurrency } from "@interfaces/currency.interface";
import { FormLabel } from "@ui/FormElements";

import { AttributeField } from "./AttributeField";
import { CommonAttributeIcon } from "./CommonAttributeIcon";
import { DEFAULT_CURRENCY_SYMBOL } from "./constants";
import { cn } from "@utils/tailwind.util";

/** Extracts only FieldArrayPath keys whose value is IAttributeFieldValue[] */
type AttributeFieldArrayPath<T extends FieldValues> = {
  [K in FieldArrayPath<T>]: FieldArrayPathValue<T, K> extends IAttributeFieldValue[] ? K : never;
}[FieldArrayPath<T>];

interface AttributesFormProps<T extends FieldValues> {
  // Field array path, e.g. "attribute_values"
  name: AttributeFieldArrayPath<T>;
  control: Control<T>;
  attributes: IAttribute[];
  // Ids of attributes shared between the company and its active deal; shows the link icon.
  commonAttributeIds?: Set<string>;
  hideCommonAttributeIcons?: boolean;
  onCommonAttributeClick?: (attribute: IAttribute) => void;
  onAttributeSave?: (value: IAttributeFieldValue, isCommon: boolean) => void;
  // Currency shown on CURRENCY attributes; falls back to the organization default.
  currency?: ICurrency | null;
  // Never offered by RELATION search — typically the entity being edited.
  excludedRelationIds?: string[];
  className?: string;
}

export function AttributesForm<T extends FieldValues>({
  name,
  control,
  attributes,
  commonAttributeIds,
  hideCommonAttributeIcons = false,
  onCommonAttributeClick,
  onAttributeSave,
  currency,
  excludedRelationIds,
  className = "flex flex-col gap-4",
}: AttributesFormProps<T>) {
  const { data: currencies } = useCurrencies();
  const currencySymbol =
    currency?.symbol ??
    currencies?.find((item) => item.order === 0)?.currency.symbol ??
    DEFAULT_CURRENCY_SYMBOL;

  const { fields: rawFields } = useFieldArray({ control, name: name as FieldArrayPath<T> });
  const fields = rawFields as unknown as (IAttributeFieldValue & { id: string })[];

  const rows = attributes
    .map((attribute) => ({
      attribute,
      index: fields.findIndex((f) => f.attribute === attribute.id),
    }))
    .filter((row) => row.index !== -1);

  return (
    <div className={cn(" mb-4", className)}>
      {rows.map(({ attribute, index }) => {
        const isCommon = !!commonAttributeIds?.has(attribute.id);
        const showCommonIcon = isCommon && !hideCommonAttributeIcons;

        return (
          <Controller
            key={attribute.id}
            control={control}
            name={`${name}.${index}` as Path<T>}
            render={({
              field: { value: fieldValue, onChange },
              fieldState: { invalid, error },
            }) => {
              const current = fieldValue as IAttributeFieldValue;

              return (
                <div className="flex flex-col gap-0.5">
                  <FormLabel htmlFor={attribute.id}>
                    <span className="flex w-full items-center justify-between gap-1">
                      <span className="min-w-0 break-words">{attribute.title}</span>
                      {showCommonIcon && (
                        <CommonAttributeIcon
                          onClick={
                            onCommonAttributeClick
                              ? () => onCommonAttributeClick(attribute)
                              : undefined
                          }
                        />
                      )}
                    </span>
                  </FormLabel>
                  <AttributeField
                    attribute={attribute}
                    value={current.value}
                    currencySymbol={currencySymbol}
                    isInvalid={invalid}
                    errorMessage={error?.message}
                    relations={current.relations}
                    excludedRelationIds={excludedRelationIds}
                    onChange={(next) => onChange({ ...current, value: next })}
                    onCommit={(next) => onAttributeSave?.({ ...current, value: next }, isCommon)}
                    // Keeps form state live while open...
                    onRelationsChange={(next) => onChange({ ...current, relations: next })}
                    // ...and saves only once it settles, so a batch of picks is a single request.
                    onRelationsSave={(next) =>
                      onAttributeSave?.({ ...current, relations: next }, isCommon)
                    }
                  />
                </div>
              );
            }}
          />
        );
      })}
    </div>
  );
}
